/**
 * Enhanced Highlight Renderer using TextAnchors
 *
 * This provides precise, XPath-based highlighting that survives DOM changes
 * Replaces the simple string replacement approach in highlightRenderer.ts
 */

import type { Note, HighlightColor } from '../components/reader/types';
import {
  type TextAnchor,
  deserializeTextAnchor,
  applyHighlightsWithAnchors,
  removeHighlight as removeHighlightAnchor,
  getHighlightColorWithOpacity,
} from './textAnchor';

/**
 * Apply highlights to a container using TextAnchors
 *
 * This should be called after the container HTML is rendered
 * It will restore precise Range selections and wrap them in highlight spans
 */
export function applyHighlightsToContainer(
  container: HTMLElement,
  highlights: Note[]
): void {
  if (!highlights || highlights.length === 0) {
    return;
  }

  // Convert Note objects to anchor format
  const anchors = highlights
    .map((note) => {
      // Try to deserialize TextAnchor from location field
      const anchor = deserializeLocationToAnchor(note.location);
      if (!anchor) {
        console.warn(`Could not deserialize anchor for highlight ${note.id}`);
        return null;
      }

      return {
        id: note.id,
        anchor,
        color: getHighlightColorWithOpacity(note.color, 0.4),
      };
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);

  // Apply all highlights
  applyHighlightsWithAnchors(container, anchors);
}

/**
 * Remove a specific highlight from the container
 */
export function removeHighlightFromContainer(
  container: HTMLElement,
  highlightId: string
): void {
  removeHighlightAnchor(highlightId, container);
}

/**
 * Deserialize location field to TextAnchor
 *
 * Location can be:
 * - JSON string (new TextAnchor format)
 * - Number (old chapter index format) - returns null
 * - String CFI (EPUB.js format) - returns null
 */
function deserializeLocationToAnchor(
  location: string | number
): TextAnchor | null {
  if (typeof location === 'number') {
    // Old format: chapter index only, no precise anchor
    return null;
  }

  if (typeof location === 'string') {
    // Try to parse as TextAnchor JSON
    try {
      return deserializeTextAnchor(location);
    } catch {
      // Not a TextAnchor, might be CFI or other format
      return null;
    }
  }

  return null;
}

/**
 * Get highlight color with opacity for better readability
 * (Re-exported from textAnchor for convenience)
 */
export { getHighlightColorWithOpacity };

/**
 * Highlight click handler
 * Call this in onClick event of the container
 */
export function handleHighlightClick(
  event: MouseEvent,
  highlights: Note[],
  onHighlightClicked: (note: Note) => void
): void {
  const target = event.target as HTMLElement;

  // Check if user is selecting text
  const selection = window.getSelection();
  if (selection && selection.toString().length > 0) {
    return;
  }

  // Check if click was on a highlight
  if (target.classList.contains('reader-highlight')) {
    const highlightId = target.getAttribute('data-highlight-id');
    if (highlightId) {
      const note = highlights.find((n) => n.id === highlightId);
      if (note) {
        onHighlightClicked(note);
      }
    }
  }
}

/**
 * Helper to check if location is a TextAnchor
 */
export function isTextAnchorLocation(location: string | number): boolean {
  if (typeof location !== 'string') {
    return false;
  }

  try {
    const parsed = JSON.parse(location);
    return (
      parsed &&
      typeof parsed === 'object' &&
      'startXPath' in parsed &&
      'endXPath' in parsed &&
      'textContext' in parsed
    );
  } catch {
    return false;
  }
}
